import React from "react";
import {Tooltip} from "antd";
import {DisconnectOutlined, LoadingOutlined} from "@ant-design/icons";
import {injectIntl} from "react-intl";
import {retryWsNow} from "../../../../util/wsStatus";
import {useLiveStatus} from "./useLiveStatus";
import "./LiveIndicator.less";

// Are new inbound messages arriving in real time? Being connected is the normal state, so with `problemsOnly`
// (next to the Chats title, and next to the contact's number on phones) nothing shows while live: only
// "Reconnecting…" / "Offline". A green dot next to a person would read as the customer being online.
const LiveIndicator = ({intl, problemsOnly = false}) => {
  const status = useLiveStatus();
  const t = (id, defaultMessage) => intl.formatMessage({id, defaultMessage});

  if (status === 'live' || status === 'connecting') {
    if (problemsOnly) return null;
    const label = status === 'live'
      ? t('chats.live', 'Live: new messages arrive instantly')
      : t('chats.connecting', 'Connecting');
    return (
      <Tooltip title={label}>
        <span className={`live-indicator live-indicator--${status}`} role="img" aria-label={label} tabIndex={0}>
          <span className="live-indicator__dot"/>
        </span>
      </Tooltip>
    );
  }

  return (
    <span className={`live-indicator live-indicator--${status}`} role="status">
      {status === 'reconnecting'
        ? <LoadingOutlined className="live-indicator__icon" aria-hidden="true"/>
        : <span className="live-indicator__dot" aria-hidden="true"/>}
      {status === 'reconnecting' ? t('chats.reconnecting', 'Reconnecting…') : t('chats.offline', 'Offline')}
    </span>
  );
};

// Above the composer once the connection has been down a while: replies may miss newer messages.
export const ConnectionBanner = injectIntl(({intl}) => {
  const status = useLiveStatus();
  if (status !== 'offline') return null;
  return (
    <div className="connection-banner" role="alert">
      <DisconnectOutlined className="connection-banner__icon" aria-hidden="true"/>
      <span className="connection-banner__text">
        {intl.formatMessage({id: 'chats.offlineBanner', defaultMessage: "New messages won't show until you reconnect."})}
      </span>
      <button type="button" className="connection-banner__retry" onClick={retryWsNow}>
        {intl.formatMessage({id: 'chats.retry', defaultMessage: 'Retry'})}
      </button>
    </div>
  );
});

export default injectIntl(LiveIndicator);
